import React from 'react'
import { AppButton } from 'components/Common/AppButton/AppButton.component'
import { AppCheckIcon } from 'components/Common/AppSvgIcons/AppCheckIcon'
import AppCrossIcon from 'components/Common/AppSvgIcons/AppModalCrossIcon'
import { UserTaskSuggestedItemStatus } from '__generated__/api-types-and-hooks'
import { AppNameInitials } from 'components/Common/AppNameInitials/AppNameInitials.component'
import { LoadingIndicator } from 'stream-chat-react'
import { AppAvatar } from 'components/Common/AppAvatar/AppAvatar.component'

// Use the Coach Logic football avatar
const CoachLogicAvatar = '/images/CL-avatar.png'

interface AppGoalSuggestedByBoxProps {
  imageUrl?: string
  onAccept: () => void
  onReject: () => void
  status: UserTaskSuggestedItemStatus
  size?: 'sm' | 'lg' | 'md' | 'xs'
  name: { firstName: string; lastName: string }
  isDisabled?: boolean
  isLoading?: boolean
  // New props for Tackle It page behavior
  assigneeName?: { firstName: string; lastName: string }
  showAwaitingAcceptance?: boolean
}

export const AppSuggestedByBox: React.FC<AppGoalSuggestedByBoxProps> = ({
  imageUrl,
  onAccept,
  onReject,
  status,
  size = 'sm',
  name,
  isDisabled,
  isLoading,
  assigneeName,
  showAwaitingAcceptance = false,
}) => {
  const isAccepted = status === UserTaskSuggestedItemStatus.Accepted
  const isRejected = status === UserTaskSuggestedItemStatus.Rejected

  // Check if this is Coach Logic
  const isCoachLogic = name.firstName === 'Coach' && name.lastName === 'Logic'

  // Check if assignee is Coach Logic
  const isAssigneeCoachLogic =
    assigneeName?.firstName === 'Coach' && assigneeName?.lastName === 'Logic'

  // Determine the display name to show
  const displayName = showAwaitingAcceptance && assigneeName ? assigneeName : name
  const displayIsCoachLogic =
    showAwaitingAcceptance && assigneeName ? isAssigneeCoachLogic : isCoachLogic

  // Extracted colors and variants based on status
  const checkIconColor = isAccepted ? '#61BA47' : '#999999'
  const crossIconColor = isRejected ? '#D0021B' : '#999999'
  const acceptButtonVariant = isAccepted ? 'success' : 'secondary'
  const rejectButtonVariant = isRejected ? 'alert' : 'secondary'
  return (
    <div className="flex items-center gap-2 mt-2">
      <div className="w-[150px]">
        <AppButton
          label={showAwaitingAcceptance ? 'Awaiting Acceptance' : 'Suggested By'}
          size={size}
          variant="secondary"
          RightComponent={
            displayIsCoachLogic ? (
              <AppAvatar src={CoachLogicAvatar} size="sm" type="image" shape="Rounded" />
            ) : (
              ((
                <AppNameInitials
                  name={displayName}
                  className="bg-app-grey-30 w-7 h-7 text-xs !font-medium !text-app-grey-70"
                />
              ) as any)
            )
          }
        />
      </div>
      <div className="flex gap-2 flex-shrink-0">
        {isLoading ? (
          <LoadingIndicator size={16} />
        ) : (
          <>
            <AppButton
              size={size}
              disabled={isDisabled || isAccepted || showAwaitingAcceptance}
              variant={acceptButtonVariant}
              LeftIcon={(props) => <AppCheckIcon {...props} color={checkIconColor} />}
              onClick={(e) => {
                e?.preventDefault()
                e?.stopPropagation()
                onAccept()
              }}
            />
            <AppButton
              size={size}
              disabled={isDisabled || isRejected || showAwaitingAcceptance}
              variant={rejectButtonVariant}
              LeftIcon={(props) => <AppCrossIcon {...props} color={crossIconColor} />}
              onClick={(e) => {
                e?.preventDefault()
                e?.stopPropagation()
                onReject()
              }}
            />
          </>
        )}
      </div>
    </div>
  )
}
